import { useQuery } from '@tanstack/react-query';
import { appQueryClient } from '../appQueryClient';
import { ApiHandler } from '../handlers';
import { Donee } from '../models';
import { mapResponseToCampuses } from '../utils';

export const DONEE_QUERY_KEY = 'currentDonee';

export type DoneeQueryData = {
    campuses: Donee[];
};

export const doneeQueryFn = async (): Promise<DoneeQueryData> => {
    const response = await ApiHandler.instance.donees.getCurrentDonee();
    if (!response.success) {
        throw new Error('Failed to fetch donee');
    }
    const campuses = mapResponseToCampuses(response.response);
    return { campuses };
};

export const useDoneeQuery = (enabled = true) => {
    return useQuery({
        queryKey: [DONEE_QUERY_KEY],
        queryFn: doneeQueryFn,
        enabled: !!enabled,
        staleTime: 1000 * 60 * 5,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const invalidateDoneeQuery = () => {
    return appQueryClient.invalidateQueries({ queryKey: [DONEE_QUERY_KEY] });
};

export const getDoneeQueryData = () => {
    return appQueryClient.getQueryData<DoneeQueryData>([DONEE_QUERY_KEY]);
};

export const setDoneeQueryData = (campuses: Donee[]) => {
    appQueryClient.setQueryData<DoneeQueryData>([DONEE_QUERY_KEY], {
        campuses,
    });
};

export const updateDoneeInCache = (
    doneeId: number,
    updater: (donee: Donee) => Donee,
) => {
    const current = getDoneeQueryData();
    if (!current?.campuses) return;
    setDoneeQueryData(
        current.campuses.map((c) => (c.id === doneeId ? updater(c) : c)),
    );
};
